import { CheckboxMultiDropdownOption } from '../../shared/components/checkbox-multi-dropdown/checkbox-multi-dropdown.component';
import { LinkNavItem } from '../../shared/components/link-nav/link-nav.component';
import { ShimmerColumn } from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import {
  DiscountEligibility,
  DiscountType,
  DiscountWashType,
  RenewalCycle,
  WeekDay,
} from '../models/discount.models';

export const DISCOUNT_TABS: LinkNavItem[] = [
  { value: 'summary', label: 'Discounts Summary' },
  { value: 'custom', label: 'Custom Discount' },
];

export const DISCOUNT_TYPE_OPTIONS: { value: DiscountType; label: string }[] = [
  { value: 'flat', label: 'Flat' },
  { value: 'percentage', label: 'Percentage' },
];

/**
 * Attendant Order Request custom-discount reasons (discounts.reason).
 * Keep in sync with svc-package `CUSTOM_DISCOUNT_REASONS`.
 */
export const CUSTOM_DISCOUNT_REASON_OPTIONS: { value: string; label: string }[] = [
  { value: 'Service Issue', label: 'Service Issue' },
  { value: 'Goodwill', label: 'Goodwill' },
  { value: 'Negotiation', label: 'Negotiation' },
  { value: 'Other', label: 'Other' },
];

export const CUSTOM_DISCOUNT_REASONS: readonly string[] = CUSTOM_DISCOUNT_REASON_OPTIONS.map(
  option => option.value
);

/** UI filter token + API value for list/checkbox filters. */
export interface DiscountListFilterOption<TApi> {
  /** Stored in applied filter state and emitted by the data-table. */
  value: string;
  label: string;
  apiValue: TApi;
}

export const DISCOUNT_TYPE_LIST_FILTER_OPTIONS: DiscountListFilterOption<DiscountType>[] =
  DISCOUNT_TYPE_OPTIONS.map(option => ({
    value: option.value,
    label: option.label,
    apiValue: option.value,
  }));

export const DISCOUNT_STATUS_LIST_FILTER_OPTIONS: DiscountListFilterOption<boolean>[] = [
  { value: 'active', label: 'Active', apiValue: true },
  { value: 'inactive', label: 'Inactive', apiValue: false },
];

export const ELIGIBILITY_OPTIONS: { value: DiscountEligibility; label: string }[] = [
  { value: 'all_customer', label: 'All Customer' },
  { value: 'first_time', label: 'First Time' },
  { value: 'returning', label: 'Returning' },
];

/**
 * Wash Type is a required checkbox multi-select. "All wash" is a UI Select All
 * that selects every individual wash type id (never sent as its own API value).
 * Create starts unselected; Edit maps from the Discount API response (All wash
 * is checked when the API returns every available wash type).
 *
 * The design also lists "Prepaid"; svc-package only accepts single_wash and
 * monthly_membership, so it is deliberately not offered here.
 */
export const WASH_TYPE_OPTIONS: CheckboxMultiDropdownOption[] = [
  { id: 'single_wash', name: 'Single Wash' },
  { id: 'monthly_membership', name: 'Monthly Membership' },
];

export const WASH_TYPE_LABELS: Record<DiscountWashType, string> = {
  single_wash: 'Single Wash',
  monthly_membership: 'Monthly Membership',
};

export const WEEK_DAYS: { id: WeekDay; name: string }[] = [
  { id: 'monday', name: 'Monday' },
  { id: 'tuesday', name: 'Tuesday' },
  { id: 'wednesday', name: 'Wednesday' },
  { id: 'thursday', name: 'Thursday' },
  { id: 'friday', name: 'Friday' },
  { id: 'saturday', name: 'Saturday' },
  { id: 'sunday', name: 'Sunday' },
];

/** Renewal cycle cards under Applicability; `standard` is the implicit default. */
export const RENEWAL_CYCLE_CARDS: {
  value: Exclude<RenewalCycle, 'standard'>;
  title: string;
  description: string;
}[] = [
  {
    value: 'advance_custom',
    title: 'Custom schedule',
    description:
      'Set a different discount for different cycles: Renewal 1, Renewal 2, etc. up to 12 renewals.',
  },
  {
    value: 'advance_recurring',
    title: 'Recurring',
    description: 'Same discount applies on every renewal, for the months you choose.',
  },
];

/**
 * User-facing DISCOUNT TYPE labels (list badge, details, CSV).
 * Advance cycles win over flat/percentage; API enums stay unchanged.
 */
export const DISCOUNT_TYPE_BADGE_LABELS: Record<string, string> = {
  flat: 'Flat',
  percentage: 'Percentage',
  advance_custom: 'Custom Schedule',
  advance_recurring: 'Recurring',
};

/** Lookup derived from ELIGIBILITY_OPTIONS — single source for list/details/CSV labels. */
export const ELIGIBILITY_LABELS: Record<DiscountEligibility, string> = Object.fromEntries(
  ELIGIBILITY_OPTIONS.map(option => [option.value, option.label])
) as Record<DiscountEligibility, string>;

/** DB caps custom schedules at renewal 12 (discount_custom_schedules check constraint). */
export const MAX_CUSTOM_SCHEDULE_RENEWALS = 12;

/**
 * Months a recurring discount can repeat for. Bounded by the Stripe coupon
 * duration_in_months the billing side uses, and by discounts_recurring_renewal_months_check.
 */
export const MAX_RECURRING_RENEWAL_MONTHS = 12;

/**
 * Minimum gap between Start/End dates and From/To restriction times
 * (matches campaign schedule UX).
 */
export const MIN_DISCOUNT_INTERVAL_MINUTES = 30;

/** discounts.name is VarChar(100) but the enhanced create schema caps it at 50. */
export const DISCOUNT_NAME_MAX_LENGTH = 50;

export const DISCOUNT_ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

export const DEFAULT_DISCOUNT_PAGE_SIZE = 10;

export const CUSTOM_SHIMMER_COLUMNS: ShimmerColumn[] = [
  { label: 'Created Date', width: 'md' },
  { label: 'Order ID', width: 'md' },
  { label: 'Customer Name', width: 'md' },
  { label: 'Discount Amount', width: 'sm' },
  { label: 'Applied By', width: 'md' },
  { label: 'Discount Name', width: 'md' },
  { label: 'Reason', width: 'md' },
];

export const SUMMARY_SHIMMER_COLUMNS: ShimmerColumn[] = [
  { label: 'Created Date', width: 'md' },
  { label: 'Discount Name', width: 'lg' },
  { label: 'Type', width: 'sm' },
  { label: 'Status', width: 'sm' },
  { label: 'Location', width: 'md' },
  { label: 'Discount amount', width: 'sm' },
  { label: 'Times Applied', width: 'sm' },
  { label: 'Edited Date', width: 'md' },
];
